"use client";

import { Clock } from "lucide-react";
import Link from "next/link";
import type { ReactNode } from "react";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import type { CreatorPortalAccess } from "../creatorAuthContext";
import { formatUsd } from "../formatUsd";
import { ROW_PRESS } from "../one/chrome";
import { requestGuaranteeUsd } from "../requestGuarantee";
import { useRequestCopy } from "./useRequestCopy";
import {
  type CreatorFeedItem,
  type RequestRowVariant,
  requestHref,
} from "./utils";

/** One request in a hairline list: who it's for, the guarantee, the time left. */
export function RequestRow({
  item,
  now,
  access,
  variant,
}: {
  item: CreatorFeedItem;
  now: Date;
  access: CreatorPortalAccess | null;
  variant: RequestRowVariant;
}) {
  const t = useCreatorTranslations("requests");
  const copy = useRequestCopy();
  return (
    <li>
      <Link
        href={requestHref(item, access)}
        className={cn(
          "flex flex-col gap-nf-ds-2 rounded-nf-ds-md px-nf-ds-3 py-nf-ds-3 outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-nf-ds-brand",
          ROW_PRESS,
        )}
      >
        <div className="flex items-center justify-between gap-nf-ds-3">
          <span className="font-nf-ds-mono text-[10.5px] uppercase tracking-[0.08em] text-muted-foreground leading-none">
            {item.orgName}
          </span>
        </div>
        <span className="font-nf-ds-sans text-nf-ds-sm font-[600] leading-[1.3]">
          {item.title}
        </span>
        <span className="font-nf-ds-sans text-nf-ds-xs text-muted-foreground">
          {t.rich("guarantee.row", {
            amount: formatUsd(requestGuaranteeUsd(item)),
            figure: (chunks: ReactNode) => (
              <span className="text-nf-ds-md font-[600] tabular-nums text-foreground">
                {chunks}
              </span>
            ),
          })}
        </span>
        <div className="flex items-center justify-between gap-nf-ds-3">
          <span className="flex items-center gap-nf-ds-1 font-nf-ds-sans text-nf-ds-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" strokeWidth={1.8} aria-hidden />
            {copy.timeLeft(item.expiresAt, now)}
          </span>
          <span className="font-nf-ds-sans text-nf-ds-xs text-nf-ds-purple-product">
            {t("row.view")}
          </span>
        </div>
      </Link>
    </li>
  );
}
